import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-thrombophilia"),
  kind: "test",
  tags: ["testing", "her", "pre-cycle", "autoimmune"],
  title: "Thrombophilia Panel",
  subtitle: "Screens for clotting disorders linked to recurrent pregnancy loss",
  body: {
    testName: "Thrombophilia Panel",
    category: "blood-marker",
    evidenceStrength: "mixed",
    whatItMeasures:
      "A broad clotting evaluation can include acquired antiphospholipid syndrome testing and inherited thrombophilia tests such as Factor V Leiden, prothrombin mutation, protein C, protein S, and antithrombin. These are not all equally supported in fertility care.",
    howItWorks:
      "A blood draw is sent to a specialized lab. APS testing usually includes lupus anticoagulant, anticardiolipin IgG/IgM, and anti-beta-2-glycoprotein I IgG/IgM, with repeat testing at least 12 weeks later if positive. Inherited thrombophilia tests may be ordered for clotting-risk management in selected patients, but ASRM does not recommend routine inherited thrombophilia testing for recurrent pregnancy loss.",
    quickFacts: [
      { kind: "sample", value: "Blood" },
      { kind: "time", value: "5 min draw" },
      { kind: "pain", value: "Needle pinch" },
      { kind: "prep", value: "Repeat APS labs if positive" },
      { kind: "results", value: "5-10 business days" },
    ],
    expectations: {
      canExpect: [
        "Blood testing for APS markers when clinical criteria are met",
        "Repeat antiphospholipid antibody testing if the first result is positive",
        "Possible hematology or rheumatology referral if APS markers are persistently moderate or high",
      ],
      shouldNotExpect: [
        "Routine inherited thrombophilia testing is not recommended for recurrent pregnancy loss",
        "Empiric aspirin or heparin is not supported when APS testing is negative",
        "A single positive antiphospholipid antibody result is not enough to diagnose APS",
        "A broad panel can create false positives, delays, and unnecessary anticoagulation",
      ],
    },
    considerations: [
      "Separate APS testing from inherited thrombophilia screening; they answer different questions",
      "Ask whether you meet clinical criteria for APS testing, such as recurrent losses, later loss, thrombosis history, or severe placental complications",
      "Ask whether miscarriages were chromosomally tested; if losses were aneuploid, APS testing may be less useful",
      "Ask what treatment would follow a positive result and who will manage anticoagulation during pregnancy",
      "Avoid MTHFR testing as part of a fertility thrombophilia workup",
    ],
    bestFor: [
      "Patients who meet clinical criteria for antiphospholipid syndrome testing",
      "Patients with personal clot history or strong family clot history, under hematology guidance",
      "Not recommended as a routine recurrent pregnancy loss panel for inherited thrombophilias",
    ],
    keyFindings: [
      {
        direction: "positive",
        outcome:
          "ASRM recommends laboratory evaluation for antiphospholipid antibodies when clinical criteria for APS are met",
        mechanism:
          "True APS is an acquired thrombophilia where targeted treatment can reduce miscarriage risk in a carefully defined group",
        source: "ASRM recurrent pregnancy loss committee opinion, 2026",
      },
      {
        direction: "negative",
        outcome:
          "ASRM does not recommend routine inherited thrombophilia testing among women with recurrent pregnancy loss",
        mechanism:
          "Recent evidence suggests inherited thrombophilia prevalence in RPL is similar to the general population, and treatment has not improved live birth",
        source: "ASRM recurrent pregnancy loss committee opinion, 2026",
      },
      {
        direction: "negative",
        outcome:
          "Low-molecular-weight heparin plus aspirin did not improve live birth compared with no anticoagulation in inherited thrombophilia and recurrent miscarriage",
        magnitude: "Live birth: 72% treated vs 71% untreated",
        source: "ALIFE2 trial summarized in ASRM 2026 RPL guidance",
      },
    ],
    headlineStat: "APS yes, inherited routine no",
    turnaroundTime: "5-10 business days",
    costRange: { low: 200, high: 800 },
    atHome: false,
    clinicRequired: false,
    dataPoints: [],
  },
  sources: [
    {
      title: "Recurrent pregnancy loss: a committee opinion",
      authors:
        "Practice Committee of the American Society for Reproductive Medicine",
      journal: "ASRM Practice Committee",
      year: 2026,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Anticoagulants for women with confirmed inherited thrombophilia and recurrent pregnancy loss (ALIFE2): an open-label, phase 3, multicentre, randomised, controlled trial",
      authors: "Quenby S, Booth K, Hiller L et al.",
      journal: "The Lancet",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37271155/",
      evidenceLevel: "rct",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);
